import { useTheme } from '@/contexts/ThemeContext';
import { TextCanvas } from '../TextCanvas';

export default function Terms(): JSX.Element {
  const host = 'liernlimited.com';
  const email = host ? `contact@${host}` : '';
  const theme = useTheme();
  const { landingPage } = theme;
  return (
    <>
      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">1. THESE TERMS</h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            1.1 What these terms cover.
          </h2>
          <p>
            These are the terms and conditions on which we supply our services
            to you.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            1.2 Why you should read them.
          </h2>
          <p>
            Please read these terms carefully before you submit your order to
            us. These terms tell you who we are, how we will provide services to
            you, how you and we may change or end the contract, what to do if
            there is a problem, and other important information. If you think
            that there is a mistake in these terms, please contact us to
            discuss.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          2. INFORMATION ABOUT US AND HOW TO CONTACT US
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">2.1 Who we are.</h2>
          <p>
            We are <TextCanvas text="Liern Ltd" textColor="#000" inline />, a
            company registered in Cyprus under company registration number{' '}
            <TextCanvas text="HE 41881" textColor="#000" inline />, and our
            registered address is{' '}
            <TextCanvas
              text="Leonidou, 8, 2007, Nicosia, Cyprus"
              textColor="#000"
              inline
            />
            .
          </p>

          <h2 className="text-lg font-semibold mb-2">2.2 How to contact us.</h2>
          <p>
            You can contact us by emailing our customer service team at {email}{' '}
            or by writing to us at our trading address{' '}
            <TextCanvas
              text="Leonidou, 8, 2007, Nicosia, Cyprus"
              textColor="#000"
              inline
            />
            .
          </p>

          <h2 className="text-lg font-semibold mb-2">
            2.3 How we may contact you.
          </h2>
          <p>
            If we have to contact you, we will do so by telephone or by writing
            to you at the email address or postal address you provided to us in
            your order.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            2.4 &quot;Writing&quot; includes emails.
          </h2>
          <p>
            When we use the words &quot;writing&quot; or &quot;written&quot; in
            these terms, this includes emails.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">3. OUR CONTRACT WITH YOU</h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            3.1 How we will accept your order.
          </h2>
          <p>
            Our acceptance of your order will take place when you submit your
            details on our website for the Service and we take payment, at which
            point a contract will come into existence between you and us.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            3.2 If we cannot accept your order.
          </h2>
          <p>
            If we are unable to accept your order, we will inform you of this in
            writing and will not charge you for the service. This might be
            because the official government website is not available to file
            your application, because of unexpected limits on our resources
            which we could not reasonably plan for, because we have identified
            an error in the price or description of the service.
          </p>

          <h2 className="text-lg font-semibold mb-2">3.3 Your order number.</h2>
          <p>
            We will assign an order number to your order and tell you what it is
            when we accept your order. It will help us if you can tell us the
            order number whenever you contact us about your order.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            3.4 Your ability to receive Services.
          </h2>
          <p>
            You warrant that:
            <span className="ml-4 block">
              3.4.1 All personal information you provide is to your knowledge
              accurate and up to date; and
            </span>
            <span className="ml-4 block">
              3.4.2 Where you choose to file an application through our website,
              you are eligible to do so and that you have complied with all
              applicable law and legislation.
            </span>
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">4. OUR PACKAGE OF SERVICES</h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">4.1 Services.</h2>
          <p>
            We offer a turnkey package of services for 12 months as follows:
            {landingPage === 'v13' ? (
              <>
                <span className="ml-4 block">
                  4.1.1 unlimited filing of the various DVLA applications as
                  described on our website;
                </span>
                <span className="ml-4 block">
                  4.1.2 unlimited calls to the legal hotline and 24/7 customer
                  service by email;
                </span>
                <span className="ml-4 block">
                  4.1.3 protection in case of loss of driving licence where, if
                  you lose your driving licence, we will allow you to submit an
                  application for a replacement free of charge via our website;
                  and
                </span>
                <span className="ml-4 block">
                  4.1.4 access to good deals related to cars and driving.
                </span>
              </>
            ) : (
              <>
                <span className="ml-4 block">
                  4.1.1 Filing of the various applications as described on our
                  website;
                </span>
                <span className="ml-4 block">
                  4.1.2 Access to 24/7 customer service by email;
                </span>
                <span className="ml-4 block">
                  4.1.3 Protection in case of loss of driving licence where, if
                  you lose your driving licence, we will allow you to submit an
                  application for a replacement free of charge via our website;
                  and
                </span>
                <span className="ml-4 block">
                  4.1.4 Access to loyalty Program with many great deals in the
                  UK.
                </span>
              </>
            )}
          </p>

          <h2 className="text-lg font-semibold mb-2">
            4.2 When we will provide the Service.
          </h2>
          <p>
            Once you have paid, you get 12 months access to the Service. Where
            you are filing an application on which you wish our assistance, you
            will enter various details about you that we will require in order
            to file the application with the relevant authority/organisation.
            Once your order has been reviewed, we will prepare the application
            you selected so that it is ready to be filed at the DVLA or any
            other applicable UK Government body. We will normally supply and
            complete the service immediately after we accept your order and
            receive your payment.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            4.3 We are not responsible for delays outside our control.
          </h2>
          <p>
            If our fulfilment of the application assistance is delayed by an
            event outside our control then we will contact you as soon as
            possible to let you know and we will take steps to minimise the
            effect of the delay. Provided we do this we will not be liable for
            delays caused by the event, but if there is a risk of substantial
            delay you may contact us to end the contract and receive a refund
            for fee you have paid for the application but not received.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            4.4 Your legal rights if we provide the Service late.
          </h2>
          <p>
            You have legal rights if we provide the Service late. You may treat
            the contract as at an end straight away if, for whatever reason, we
            fail to give you access to the Service within 1 day of you paying.
            If you do choose to treat the contract as at an end for late
            delivery under, you can cancel your order for the application and we
            will refund the amount you have paid.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            4.5 What will happen if you do not give required information to us.
          </h2>
          <p>
            We need certain information from you so that we can supply the
            Service to you, for example, your name, address, driving licence
            details and this will have been stated in the description of the
            application on our website. If you give us incomplete or incorrect
            information, we may not be able to provide you the Service properly.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            4.6 Not affiliated with any UK government body/organisation.
          </h2>
          <p>
            Please note that we are not affiliated with the Driver and Vehicle
            Licensing Agency (DVLA), the UK Government, or any official body. We
            are a third-party company offering a turnkey package of services
            including complete online processing and validation service on your
            applications. The Services provided are not mandatory when
            submitting any such applications, including but not limited to
            applying for a driving licence which is available from the official
            government agency portal, which is the official DVLA website, with
            which we are not affiliated, for a lower cost or free of charge. The
            fee charged for our Service is additional to any applicable DVLA or
            other UK Government/official body fee.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          5. YOUR RIGHTS TO MAKE CHANGES
        </h1>
        <div className="pl-4">
          <p>
            If you wish to make a change to the service you have ordered please
            contact us. We will let you know if the change is possible. If it is
            possible we will let you know about any changes to the price of the
            service, the timing of supply or anything else which would be
            necessary as a result of your requested change and ask you to
            confirm whether you wish to go ahead with the change. If we cannot
            make the change or the consequences of making the change are
            unacceptable to you, you may want to end the contract (see clause
            6.3 - Your rights to end the contract).
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          6. OUR RIGHTS TO MAKE CHANGES
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            6.1 Minor changes to the service.
          </h2>
          <p>
            We may change the Services:
            <span className="ml-4 block">
              6.1.1 to reflect changes in relevant laws and regulatory
              requirements; and
            </span>
            <span className="ml-4 block">
              6.1.2 to implement minor technical adjustments and improvements,
              for example to address a security threat. These changes will not
              affect your use of the Services.
            </span>
          </p>

          <h2 className="text-lg font-semibold mb-2">
            6.2 More significant changes to the Services and these terms.
          </h2>
          <p>
            We may withdraw or suspend the Services for whatever reason at any
            time, including dealing with technical problems or making changes.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            6.3 Your rights if we suspend the Services.
          </h2>
          <p>
            We will contact you in advance to tell you we will be suspending
            supply of the Services, unless the problem is urgent or an
            emergency. If we have taken payment for the cost of filing an
            application but not provided it to you, we will provide a full
            refund. If we withdraw the Service, we will give you a pro-rata
            refund for any time you left on the Service.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          7. YOUR RIGHTS TO END THE CONTRACT
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            7.1 You can end your contract with us.
          </h2>
          <p>
            Your rights when you end the contract will depend on what you have
            bought, whether there is anything wrong with it, how we are
            performing, and when you decide to end the contract:
            <span className="ml-4 block">
              7.1.1 If we have not fulfilled the Services properly you may have
              a legal right to end the contract or for us to re-perform the
              Services or to get some or all of your money back), see clause 10;
            </span>
            <span className="ml-4 block">
              7.1.2 If you want to end the contract because of something we have
              done or have told you we are going to do, see clause 7.2;
            </span>
          </p>
        </div>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            7.2 Ending the contract because of something we have done or are
            going to do. If you are ending a contract for a reason set out at
            clauses 7.2.1 to 7.2.4 below the contract will end immediately and
            we will refund you in full for any application which we have not
            fulfilled. The reasons are:
          </h2>

          <ol className="list-decimal pl-6 mb-4">
            <li>
              <strong>7.2.1</strong> we have told you about an upcoming change
              to the Services or these terms which you do not agree to (see
              clause 6.2);
            </li>
            <li>
              <strong>7.2.2</strong> we have told you about an error in the
              price or description of the Service you have ordered, and you do
              not wish to proceed;
            </li>
            <li>
              <strong>7.2.3</strong> there is a risk that fulfillment of an
              application may be significantly delayed because of events outside
              our control;
            </li>
            <li>
              <strong>7.2.4</strong> you have a legal right to end the contract
              because of something we have done wrong (including because we have
              delivered late - see clause 4.4).
            </li>
          </ol>
        </div>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            7.3 Exercising your right to change your mind (Consumer Contracts
            Regulations 2013).
          </h2>
          <p>
            For most products bought online you have a legal right under the
            Consumer Contracts Regulations 2013 to change your mind and receive
            a refund. You have 14 days after the day we email you to confirm we
            have set up your Services account to cancel your account.
          </p>
        </div>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            7.4 When you don&apos;t have the right to change your mind. Once we
            have provided any part of the Services you cannot change your mind,
            even if the 14-day period is still running.
          </h2>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          8. HOW TO END THE CONTRACT WITH US (INCLUDING IF YOU HAVE CHANGED YOUR
          MIND)
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            8.1 Tell us you want to end the contract.
          </h2>
          <p>
            To end the contract with us, please let us know by doing one of the
            following:
            <span className="ml-4 block">
              8.1.1 Email. Email customer services at {email}. Please provide
              your name, home address, details of the order and, where
              available, your phone number and email address.
            </span>
            <span className="ml-4 block">
              8.1.2 Online. Complete the form on our website.
            </span>
          </p>
        </div>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            8.2 How we will refund you. Provided we have not already supplied
            any part of the Services to you, we will refund you the price you
            paid for the Service, by the method you used for payment.
          </h2>
        </div>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            8.3 When your refund will be made. We will make any refunds due to
            you within 14 days of your telling us you have changed your mind.
          </h2>
        </div>
      </section>
      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          9. OUR RIGHTS TO END THE CONTRACT
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            9.1 We may end the contract if you break it.
          </h2>
          <p>
            We may end the contract for the Service at any time by writing to
            you if:
            <span className="ml-4 block">
              9.1.1 the DVLA or any other applicable UK Government body ceases
              to allow us to provide the Service;
            </span>
            <span className="ml-4 block">
              9.1.2 you die or, by reason of illness or incapacity (whether
              mental or physical), become incapable of being the holder of a
              driving licence for whatever reason;
            </span>
            <span className="ml-4 block">
              9.1.3 you do not make any payment to us when it is due and you
              still do not make payment within 7 days of us reminding you that
              payment is due; or
            </span>
            <span className="ml-4 block">
              9.1.4 you do not, within a reasonable time of us asking for it,
              provide us with information that is necessary for us to provide
              the service.
            </span>
          </p>

          <h2 className="text-lg font-semibold mb-2">
            9.2 We may withdraw the Service. We may write to you to let you know
            that we are going to stop providing the Service.
          </h2>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          10. IF THERE IS A PROBLEM WITH THE SERVICE
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            10.1 How to tell us about problems.
          </h2>
          <p>
            If you have any questions or complaints about the service, please
            contact us. You can email us at {email}.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            10.2 Summary of your legal rights.
          </h2>
          <p>
            We are under a legal duty to supply products that are in conformity
            with this contract. See the box below for a summary of your key
            legal rights in relation to the product. Nothing in these terms will
            affect your legal rights.
          </p>

          <div className="bg-gray-200 p-4 rounded-md">
            <p className="font-semibold">Summary of your key legal rights</p>
            <p>
              This is a summary of your key legal rights. These are subject to
              certain exceptions. For detailed information please visit the
              Citizens Advice website www.adviceguide.org.uk or call 03454 04 05
              06.
            </p>
            <p>
              For services, the Consumer Rights Act 2015 says:
              <span className="ml-4 block">
                a) You can ask us to repeat or fix a service if it&apos;s not
                carried out with reasonable care and skill, or get some money
                back if we can&apos;t fix it.
              </span>
              <span className="ml-4 block">
                b) If you haven&apos;t agreed a price beforehand, what
                you&apos;re asked to pay must be reasonable.
              </span>
              <span className="ml-4 block">
                c) If you haven&apos;t agreed a time beforehand, it must be
                carried out within a reasonable time.
              </span>
              <span className="ml-4 block">
                See also Exercising your right to change your mind (Consumer
                Contracts Regulations 2013).
              </span>
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">11. PRICE AND PAYMENT</h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            11.1 Where to find the price for the product.
          </h2>
          <p>
            We charge a flat fee for the package of Services (which includes
            VAT) plus the cost of any DVLA or any other applicable UK Government
            body applications (for which VAT is not payable) you file through
            our website (except where it is a replacement covered by under
            paragraph 4.1.3). We take all reasonable care to ensure that the
            price of the service advised to you is correct. However, please see
            paragraph 11.3 for what happens if we discover an error in the price
            of the product you order. Cost of Services.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            11.2 We will pass on changes in the rate of VAT.
          </h2>
          <p>
            As the price of the Service is payable upfront there will be no
            additional VAT to pay even if the rate of VAT changes afterwards.
            However, if the rules change meaning VAT must be paid on the DVLA or
            any other applicable UK Government body fee, we will inform at the
            point you make the application and charge you this amount.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            11.3 What happens if we got the price wrong.
          </h2>
          <p>
            It is always possible that, despite our best efforts, some of the
            products we sell may be incorrectly priced. We will normally check
            prices before accepting your order so that, where the product&apos;s
            correct price at your order date is less than our stated price at
            your order date, we will charge the lower amount. If the
            product&apos;s correct price at your order date is higher than the
            price stated to you, we will contact you for your instructions
            before we accept your order. If we accept and process your order
            where a pricing error is obvious and unmistakeable and could
            reasonably have been recognised by you as a mispricing, we may end
            the contract, refund you any sums you have paid and require the
            return of any goods provided to you.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            11.4 When you must pay and how you must pay.
          </h2>
          <p>
            You must pay the full price upfront before we start providing the
            service. We will provide you with a receipted invoice after you have
            paid. We accept payment with Visa and Mastercard.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            11.5 What to do if you think an invoice is wrong.
          </h2>
          <p>
            If you think an invoice is wrong please contact us promptly to let
            us know. You will not have to pay any interest until the dispute is
            resolved. Once the dispute is resolved we will charge you interest
            on correctly invoiced sums from the original due date.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          12. OUR RESPONSIBILITY FOR LOSS OR DAMAGE SUFFERED BY YOU
        </h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            12.1 Refund for failure to comply.
          </h2>
          <p>
            If we fail to comply with these terms or provide the Service
            properly, we will refund you the DVLA or any other applicable UK
            Government body application fee (if any) and, if this happens within
            the first 14 days, we will refund you the Service fee too.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            12.2 We do not exclude or limit in any way our liability to you
            where it would be unlawful to do so.
          </h2>
          <p>
            This includes liability for death or personal injury caused by our
            negligence or the negligence of our employees, agents or
            subcontractors; for fraud or fraudulent misrepresentation; for
            breach of your legal rights in relation to the service as summarised
            at paragraph 10.2.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            12.3 We are not liable for business losses.
          </h2>
          <p>
            We only supply the products for domestic and private use. If you use
            the products for any commercial, business or re-sale purpose we will
            have no liability to you for any loss of profit, loss of business,
            business interruption, or loss of business opportunity.
          </p>
          <h2 className="text-lg font-semibold mb-2">
            12.4 We dont compensate you for all losses caused by us or our
            products{' '}
          </h2>

          <ul className="list-disc pl-8">
            <p>
              {"We're"} responsible for losses you suffer caused by us breaking
              this contract unless the loss is:
            </p>
            <li className="mb-2">
              Unexpected. It was not obvious that it would happen and nothing
              you said to us before we accepted your order meant we should have
              expected it (so, in the law, the loss was unforeseeable).
            </li>
            <li className="mb-2">
              Caused by a delaying event outside our control.{"We're"} not
              responsible for delays outside our control.
            </li>
            <li className="mb-2">
              Avoidable. Something you could have avoided by taking reasonable
              action.
            </li>
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">
          13. HOW WE MAY USE YOUR PERSONAL INFORMATION
        </h1>
        <div className="pl-4">
          <p>
            <span className="font-semibold">
              How we may use your personal information.
            </span>{' '}
            We will only use your personal information as set out in our Privacy
            Policy.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">14. OTHER IMPORTANT TERMS</h1>
        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            14.1 We may transfer this agreement to someone else.
          </h2>
          <p>
            We may transfer our rights and obligations under these terms to
            another organisation. We will always tell you in writing if this
            happens and we will ensure that the transfer will not affect your
            rights under the contract.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.2 You may not transfer your rights to someone else.
          </h2>
          <p>
            You may not transfer your rights or your obligations under these
            terms to another person.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.3 Nobody else has any rights under this contract.
          </h2>
          <p>
            This contract is between you and us. No other person shall have any
            rights to enforce any of its terms. Neither of us will need to get
            the agreement of any other person in order to end the contract or
            make any changes to these terms.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.4 If a court finds part of this contract illegal, the rest will
            continue in force.
          </h2>
          <p>
            Each of the paragraphs of these terms operates separately. If any
            court or relevant authority decides that any of them are unlawful,
            the remaining paragraphs will remain in full force and effect.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.5 Even if we delay in enforcing this contract, we can still
            enforce it later.
          </h2>
          <p>
            If we do not insist immediately that you do anything you are
            required to do under these terms, or if we delay in taking steps
            against you in respect of your breaking this contract, that will not
            mean that you do not have to do those things and it will not prevent
            us taking steps against you at a later date. For example, if you
            miss a payment and we do not chase you but we continue to provide
            the products, we can still require you to make the payment at a
            later date.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.6 Intellectual property rights.
          </h2>
          <p>
            All intellectual property rights in, arising out of or in connection
            with the Site and our Services shall be owned by us. You acknowledge
            that, in respect of any third party intellectual property rights in
            the Services, your use of intellectual property rights is
            conditional on us obtaining a written licence from the relevant
            licensor on such terms as will entitle us to license such rights to
            you.
          </p>

          <h2 className="text-lg font-semibold mb-2">
            14.7 Which laws apply to this contract and where you may bring legal
            proceedings.
          </h2>
          <p>
            These terms are governed by Cyprus law and you can bring legal
            proceedings in respect of the products in the Cyprus courts.
          </p>
        </div>
      </section>
      <section className="mb-8">
        <h1 className="text-2xl font-bold mb-4">Last updated: 10 May 2023</h1>
        <h1 className="text-2xl font-bold mb-4">Cancellation Form</h1>
        <p>
          (Complete and return this form only if you wish to withdraw from the
          contract)
        </p>

        <div className="pl-4">
          <h2 className="text-lg font-semibold mb-2">
            To: <TextCanvas text="Liern Ltd" textColor="#000" inline />,
          </h2>
          <p>
            I hereby give notice that I cancel my contract for the Services
            ordered on the following date ............
          </p>

          <ul className="list-disc pl-8">
            <li className="mb-2">
              <span className="font-semibold">Name of consumer(s):</span>{' '}
              ...................
            </li>
            <li className="mb-2">
              <span className="font-semibold">Address of consumer(s):</span>{' '}
              ...................
            </li>
            <li className="mb-2">
              <span className="font-semibold">
                Signature of consumer(s) (only if this form is notified on
                paper),
              </span>
            </li>
            <li>
              <span className="font-semibold">Date:</span> ...................
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}
